import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import http from '../../utils/request'
import { toast } from 'react-toastify'

// 🔗 Endpoint base relativo (singular) según backend
const API_URL = `/product`

// 🧠 Obtener todos los productos
export const getProducts = createAsyncThunk('product/getAll', async (_, thunkAPI) => {
  try {
    const { data } = await http.get(`${API_URL}/all-products`)
    return data
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.msg || error.message)
  }
})

// ➕ Crear producto
export const productCreate = createAsyncThunk('product/create', async (productData, thunkAPI) => {
  try {
    const { data } = await http.post(`${API_URL}/add-product`, productData)
    toast.success('✅ Producto agregado correctamente')
    return data
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || 'Error desconocido';
    toast.error(`❌ Error al crear producto: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg)
  }
})

// ✏️ Editar producto
export const editProduct = createAsyncThunk('product/edit', async (productData, thunkAPI) => {
  try {
    const { data } = await http.patch(`${API_URL}/update-product`, productData)
    toast.success('✏️ Producto actualizado correctamente')
    return data
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || 'Error desconocido';
    toast.error(`❌ Error al editar producto: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg)
  }
})

// 🗑️ Eliminar producto
export const removeProduct = createAsyncThunk('product/remove', async (id, thunkAPI) => {
  try {
    await http.delete(`${API_URL}/delete/${id}`)
    toast.info('🗑️ Producto eliminado')
    return id
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.response?.data?.msg || error.message || 'Error desconocido';
    toast.error(`❌ Error al eliminar producto: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg)
  }
})

// 🧱 Slice
const initialState = {
  products: [],
  isLoading: false,
  isError: false,
  message: '',
  // Form fields
  isEditing: false,
  editProductId: null,
  name: '',
  stock: 0,
  image: '',
  price: 0,
  costPrice: 0,
  margin: 0,
  barcode: '',
  category: '',
  user: '',
  alicuotaIVACode: 5,
  // Variants support
  variants: [], // { sku, attributes: { color, size }, stock, price, costPrice }
}

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    resetProduct: () => initialState,
    handleChange: (state, { payload: { name, value } }) => {
      state[name] = value
    },
    clearValues: () => {
      return {
        ...initialState,
        products: [], // Keep products? No, usually clearValues resets form but keeps list.
        // Actually typically clearValues resets the FORM fields.
        // Let's preserve products and loading state, reset form only.
        isEditing: false,
        editProductId: null,
        name: '',
        stock: 0,
        image: '',
        price: 0,
        costPrice: 0,
        margin: 0,
        barcode: '',
        category: '',
        alicuotaIVACode: 5,
        variants: []
      }
      // Note: simplistic reset. A better way: return { ...state, isEditing: false, ...defaults }
    },
    setEditProduct: (state, { payload }) => {
      return { ...state, isEditing: true, ...payload }
    }
  },
  extraReducers: (builder) => {
    builder
      // 📦 GET
      .addCase(getProducts.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.isLoading = false
        state.products = action.payload
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })
      // ➕ CREATE
      .addCase(productCreate.fulfilled, (state, action) => {
        state.products.push(action.payload)
        // Clear form on success
        state.isEditing = false
        state.name = ''
        state.stock = 0
        state.image = ''
        state.price = 0
        state.costPrice = 0
        state.margin = 0
        state.barcode = ''
        state.category = ''
        state.variants = []
      })
      // ✏️ EDIT
      .addCase(editProduct.fulfilled, (state, action) => {
        const index = state.products.findIndex((p) => p._id === action.payload._id)
        if (index !== -1) state.products[index] = action.payload
        // Clear form
        state.isEditing = false
        state.editProductId = null
        state.name = ''
        state.stock = 0
        state.image = ''
        state.price = 0
        state.costPrice = 0
        state.margin = 0
        state.barcode = ''
        state.category = ''
        state.variants = []
      })
      // 🗑️ DELETE
      .addCase(removeProduct.fulfilled, (state, action) => {
        state.products = state.products.filter((p) => p._id !== action.payload)
      })
  },
})

export const { resetProduct, handleChange, clearValues, setEditProduct } = productSlice.actions
export default productSlice.reducer
